import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { Button } from 'components/styled/button'
import { mapLanguageToComponentProps } from 'interview/i18n'
import { updateInterviewState } from 'interview/actions/sequenced-interview'
import styled, { css } from 'styled-components'
import { Flex } from 'components/styled/containers/flex'
import {
  getCandidateInfoForAmplitude,
  getCompanyAndInterviewInfoForAmplitude,
} from 'selectors'

const propTypes = {
  i18n: PropTypes.shape({
    sequencedInterview: PropTypes.shape({
      questionAnswerView: PropTypes.shape({
        submitAndFinish: PropTypes.string,
        submitAndNext: PropTypes.string,
        submitSuccess: PropTypes.string,
        stop: PropTypes.string,
        skipText: PropTypes.string,
        skipTextQuestion: PropTypes.string,
      }),
    }),
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  streaming: PropTypes.bool.isRequired,
  recording: PropTypes.bool.isRequired,
  answerSaved: PropTypes.bool.isRequired,
  reconnecting: PropTypes.bool.isRequired,
  candidateState: PropTypes.string.isRequired,
  interviewState: PropTypes.shape({
    currentQuestion: PropTypes.shape({
      preparationTime: PropTypes.number,
      textQuestion: PropTypes.string,
    }),
    stopped: PropTypes.bool,
    onAnswer: PropTypes.bool.isRequired,
    index: PropTypes.number.isRequired,
    toNextCountdown: PropTypes.bool.isRequired,
    answerCountdown: PropTypes.bool.isRequired,
    timerCount: PropTypes.number.isRequired,
    loading: PropTypes.bool,
  }).isRequired,
  submitting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onStop: PropTypes.func.isRequired,
  lastQuestion: PropTypes.bool.isRequired,
  errorType: PropTypes.string,
  candidate: PropTypes.shape({
    uuid: PropTypes.string,
    _id: PropTypes.string,
    opening: PropTypes.shape({
      company: PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
  }).isRequired,
  flippedView: PropTypes.bool,
}

const defaultProps = {
  errorType: '',
  flippedView: false,
}

const mapStateToProps = state => ({
  streaming: state.global.opentok.streaming,
  recording: state.global.opentok.recording,
  answerSaved: state.interview.candidate.answerSaved,
  reconnecting: state.global.opentok.reconnecting,
  onAnswer: state.interview.sequencedInterview.interviewState.onAnswer,
  errorType: state.interview.sequencedInterview.errorType,
  interviewState: state.interview.sequencedInterview.interviewState,
  candidateState: state.interview.candidate.state,
  candidate: state.candidate,
})

function renderButtonContent(
  i18n,
  recording,
  answerSaved,
  stopped,
  lastQuestion
) {
  const submitted = answerSaved && !recording

  if (stopped) {
    return lastQuestion
      ? i18n.sequencedInterview.questionAnswerView.submitAndFinish
      : i18n.sequencedInterview.questionAnswerView.submitAndNext
  }

  if (submitted) {
    return i18n.sequencedInterview.questionAnswerView.submitSuccess
  }

  return i18n.sequencedInterview.questionAnswerView.stop
}

const SubmitButtonStyle = css`
  position: relative;
  margin-top: ${({ theme }) => theme.space.s16};
`

const OverlayButtonStyle = css`
  position: absolute;
  transform: translate(-50%);
  bottom: ${({ theme }) => theme.space.s14};
  left: 50%;
`

const StreamButtonContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  z-index: 2;

  ${({ isSubmitButton }) =>
    isSubmitButton ? SubmitButtonStyle : OverlayButtonStyle};
`
export function StreamButton({
  i18n,
  interviewState,
  recording,
  onStop,
  streaming,
  reconnecting,
  dispatch,
  submitting,
  answerSaved,
  onSubmit,
  lastQuestion,
  errorType,
  candidate,
  flippedView,
}) {
  const {
    onAnswer,
    timerCount,
    answerCountdown,
    currentQuestion,
    stopped,
    loading,
  } = interviewState
  const submitted = answerSaved && !recording

  const interview = candidate && candidate.opening
  const candidateInfo = getCandidateInfoForAmplitude(candidate)
  const companyAndInterviewInfo =
    getCompanyAndInterviewInfoForAmplitude(interview)

  const getEventId = () => {
    if (stopped) {
      return lastQuestion
        ? 'submitVideoAndFinishInterviewButton'
        : 'submitVideoButton'
    }

    return 'stopRecordingButton'
  }

  const eventId = getEventId()
  const iconRight = flippedView ? 'west' : 'east'

  if (streaming && !reconnecting && currentQuestion) {
    const countdown = currentQuestion.preparationTime - 5
    if (onAnswer && !answerCountdown) {
      return (
        <StreamButtonContainer isSubmitButton={stopped || submitted}>
          <Button
            className="recording-button"
            icon={recording ? 'stop' : undefined}
            iconRight={
              stopped && !loading && !submitting ? iconRight : undefined
            }
            onClick={stopped ? onSubmit : onStop}
            disabled={submitted || (onAnswer && !stopped && !recording)}
            loading={(loading || submitting) && !errorType}
            text={renderButtonContent(
              i18n,
              recording,
              answerSaved,
              stopped,
              lastQuestion
            )}
            event={{
              locationId: 'timedInterviewPage',
              id: eventId,
              properties: {
                candidateId: candidate._id,
                ...candidateInfo,
                ...companyAndInterviewInfo,
              },
            }}
          />
        </StreamButtonContainer>
      )
    }
    if (!onAnswer && timerCount < countdown) {
      return (
        <StreamButtonContainer isSubmitButton={false}>
          <Button
            className="skip-preparation-button"
            icon="radio_button_checked"
            text={i18n.sequencedInterview.questionAnswerView.skipText}
            onClick={() =>
              dispatch(updateInterviewState({ timerCount: countdown }))
            }
            event={{
              locationId: 'timedInterviewPage',
              id: 'skipPreparationAndStartRecrodingButton',
              properties: {
                ...candidateInfo,
                ...companyAndInterviewInfo,
              },
            }}
          />
        </StreamButtonContainer>
      )
    }
  }
  return null
}

StreamButton.propTypes = propTypes
StreamButton.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  StreamButton
)
